import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button } from '../primitives/button.js';
import { Combobox } from '../primitives/combobox.js';
import { Input } from '../primitives/input.js';

export interface CrmFieldPickerOption {
  value: string;
  label: string;
  description?: string;
}

export interface CrmFieldPickerProps {
  /** '' = no value. */
  value: string;
  onValueChange: (value: string) => void;
  /** Values read from the CRM (properties, options, owners, pipelines, stages…). Never invented. */
  options: readonly CrmFieldPickerOption[];
  /** ready: `options` came from the CRM · loading · unavailable: no list (missing scope / CRM down) → free text only. */
  state?: 'ready' | 'loading' | 'unavailable';
  'aria-label': string;
  /** Injected by FormField. */
  id?: string;
  'aria-describedby'?: string;
  placeholder?: string;
  /** Shows a choice that clears the value (optional fields). */
  clearLabel?: string;
  disabled?: boolean;
  /** Width of the control (e.g. w-56). */
  className?: string;
}

const NOT_FOUND = 'No encontrado en el CRM';

/**
 * Searchable picker for a CRM-side value of the mapping, fed by the CRM's metadata. Always keeps a free-text fallback
 * («Usar otro valor») and always shows the stored value — a value missing from the CRM list is flagged, never dropped.
 * Without metadata it is a plain text field, so editing never depends on the CRM answering.
 */
export function CrmFieldPicker({ value, onValueChange, options, state = 'ready', placeholder, clearLabel, disabled, className, ...rest }: CrmFieldPickerProps) {
  const label = rest['aria-label'];
  const aria = { id: rest.id, 'aria-label': label, 'aria-describedby': rest['aria-describedby'] };
  const [freeText, setFreeText] = React.useState(false);
  const hasList = state === 'ready' && options.length > 0;
  const listMode = hasList && !freeText;
  const unknown = hasList && value !== '' && !options.some((o) => o.value === value);
  const listOptions = unknown ? [{ value, label: `${value} (no encontrado en el CRM)` }, ...options] : options;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {listMode ? (
        <Combobox
          {...aria}
          className={cn('w-56', className)}
          value={value || null}
          onValueChange={(v) => onValueChange(v ?? '')}
          options={listOptions}
          placeholder={placeholder ?? 'Elegir del CRM…'}
          searchPlaceholder="Buscar…"
          emptyText="Sin coincidencias. Usa «Usar otro valor»."
          clearLabel={clearLabel}
          disabled={disabled}
        />
      ) : (
        <Input {...aria} className={cn('w-56', className)} value={value} placeholder={state === 'loading' ? 'Cargando campos del CRM…' : placeholder} onChange={(e) => onValueChange(e.target.value)} disabled={disabled} />
      )}
      {hasList && !disabled ? (
        <Button type="button" size="sm" variant="ghost" aria-label={`${listMode ? 'Usar otro valor' : 'Elegir del CRM'}: ${label}`} onClick={() => setFreeText(listMode)}>
          {listMode ? 'Usar otro valor' : 'Elegir del CRM'}
        </Button>
      ) : null}
      {unknown ? <span className="text-meta text-warning">{NOT_FOUND}</span> : null}
    </div>
  );
}
